import moment from 'moment';

import fetchGuideCollections from 'actions/guide_performance/fetch_guide_collections';
import isFeatureEnabled from 'actions/lib/is_feature_enabled';
import grantsAiAccess from 'actions/lib/grants_ai_access';
import UserLogin from 'models/location/user_login';
import { OrganizationType } from 'models/organization';
import { ComplianceAdminRole, SystemAdminRole, TeamManagerRole } from 'models/role';
import OrganizationConverter from 'scripts/application/dto_converters/organization_converter';
import { NotExistError } from 'scripts/application/lib/error';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_silent_handler';
import analytics from 'scripts/lib/analytics';

const LOGIN_REDIRECT_ROLE_IDS = [SystemAdminRole.id, ComplianceAdminRole.id, TeamManagerRole.id];

export default class RequestOrganization {
  constructor(context) {
    this.context = context;
  }

  run() {
    const { auth, organization: organizationStore } = this.context.stores;
    const { organizations } = this.context.gateways;

    const orgId = auth.get().getOrgId();
    organizationStore.setLoading();

    // Capture whether this is a login/SSO flow *before* the async org fetch
    // below. `completeStartup` dispatches this action and then synchronously
    // calls `navigateToPreAuthPath`, which replaces `currentLocation` with the
    // pre-auth path. Reading `currentLocation` after the fetch resolves would
    // miss the UserLogin/SSO window, so the onboarding redirect would never fire
    // for password logins. See redirectSelfServeOnboardingOrg.
    const isLoginFlow = this.isLoginFlow();

    let redirected = false;

    return organizations
      .fetch({ orgId })
      .then(dto => {
        const organization = OrganizationConverter.fromDto(dto);
        organizationStore.set(organization);
        analytics.updateUser({ loginDomain: organization.loginDomain });

        const trialEndDate = organization.getTrialEndDate();
        window.cioanalytics?.identify(auth.get().getUserId(), {
          trialEndDate: trialEndDate ? moment(trialEndDate).unix() : null,
        });

        return this.redirectSelfServeOnboardingOrg(isLoginFlow).then(didRedirect => {
          if (didRedirect) {
            redirected = true;
            return;
          }

          return this.updateSidekickStatus(organization).then(() => {
            redirected = this.redirectStandaloneOrg(organization, isLoginFlow);
          });
        });
      })
      .then(() => {
        if (redirected) return;
        fetchGuideCollections(this.context);
      })
      .catch(err => {
        handleCommonErrors(this.context, err);
      })
      .finally(() => {
        organizationStore.resetLoading();
      });
  }

  isOnboardingRolloutEligible() {
    // Eligibility is flag-based, not org-type based: `onboardingHome` is the
    // eng rollout flag, and `shopifySourcedDeal` (set by ps-org-creator for
    // source === 'shopify') or `smbSelfServe` (set per-org in org manager for an
    // inked SMB self-serve deal) marks an eligible org. Shopify self-serve orgs
    // are created as TRIAL — not OrganizationType.STANDALONE — so an org-type
    // gate would miss them. STANDALONE orgs are handled by redirectStandaloneOrg.
    const rolloutEnabled = isFeatureEnabled(this.context, 'onboardingHome');
    const eligible =
      isFeatureEnabled(this.context, 'shopifySourcedDeal') || isFeatureEnabled(this.context, 'smbSelfServe');
    return rolloutEnabled && eligible;
  }

  isLoginFlow() {
    // Whether the current navigation is a fresh login or SSO landing. Must be
    // read synchronously at the start of `run`, before the async org fetch —
    // `navigateToPreAuthPath` mutates `currentLocation` in the same tick the
    // action is dispatched.
    const currentLocation = this.context.stores.currentLocation.get();
    return currentLocation instanceof UserLogin || currentLocation.isFromSso;
  }

  redirectSelfServeOnboardingOrg(isLoginFlow) {
    // Route self-serve onboarding merchants to the glados onboarding home on
    // login. `isLoginFlow` is captured by the caller before the org fetch (see
    // `run`); the remaining gates (flags, role) are synchronous, so the
    // onboarding-status endpoint is only hit for the narrow set of eligible
    // admin merchants on login — never on ordinary navigation.
    //
    // Returns a promise resolving to whether a redirect was issued.
    if (!this.isOnboardingRolloutEligible()) {
      return Promise.resolve(false);
    }

    if (!isLoginFlow || !this.hasLoginRedirectRole()) {
      return Promise.resolve(false);
    }

    return this.onboardingRedirectPath().then(path => {
      this.context.router.navigateToPath(path);
      return true;
    });
  }

  onboardingRedirectPath() {
    // Rollout and eligibility are already confirmed by
    // redirectSelfServeOnboardingOrg. Land the merchant on the recoverable
    // onboarding home until onboarding is complete; both paths are served by
    // glados on the same origin, so this is a full-window route with the
    // session cookie inherited — no hand-off work.
    return this.hasCompletedOnboarding().then(completed => {
      return completed ? '/sidekick/communication-sessions' : '/sidekick/getting-started';
    });
  }

  hasCompletedOnboarding() {
    // Whether the merchant has gone live, per glados' onboarding-status
    // endpoint (derived from routing state — glados persists no completion
    // marker). Returns a promise resolving to a boolean.
    //
    // Never call glados unless this org is in the onboarding rollout: the
    // endpoint only serves eligible self-serve merchants, so gate the request
    // on the full flag set (`onboardingHome` + `shopifySourcedDeal`/
    // `smbSelfServe`) at the call boundary. `redirectSelfServeOnboardingOrg`
    // checks the same gate (plus login flow + role) upstream, so this is
    // defense-in-depth that keeps the network call correct regardless of caller.
    if (!this.isOnboardingRolloutEligible()) {
      return Promise.resolve(false);
    }

    // Fail safe: any error resolves to false so the merchant lands on the
    // recoverable onboarding home rather than blocking login; we never rethrow
    // into the login `.catch`. A 404 (NotExistError) is the expected
    // "not gone live yet" signal and is swallowed silently. This gateway has no
    // observer, so any other error is reported via `handleCommonErrors`
    // (console + Sentry) rather than vanishing.
    return this.context.gateways.onboardingStatus
      .fetch()
      .then(dto => Boolean(dto && dto.completed))
      .catch(err => {
        if (!(err instanceof NotExistError)) {
          handleCommonErrors(this.context, err);
        }
        return false;
      });
  }

  redirectStandaloneOrg(organization, isLoginFlow) {
    // STANDALONE orgs bought Gladly AI without Gladly Team, so admins land in
    // Gladly AI on login. Runs after the onboarding redirect, which takes
    // precedence while a self-serve merchant is still onboarding, and after
    // updateSidekickStatus: an org type alone doesn't mean glados has the org
    // provisioned, and without it the destination is an error page.
    if (organization.type !== OrganizationType.STANDALONE || !organization.isSidekickEnabled) {
      return false;
    }

    if (!isLoginFlow || !this.hasLoginRedirectRole()) {
      return false;
    }

    this.context.router.navigateToPath('/sidekick/communication-sessions');
    return true;
  }

  hasLoginRedirectRole() {
    const roleIds = this.context.stores.auth.get().getRoleIds() || [];
    return roleIds.some(id => LOGIN_REDIRECT_ROLE_IDS.includes(id));
  }

  updateSidekickStatus(organization) {
    if (
      !isFeatureEnabled(this.context, 'viewGuidePerformance') &&
      !isFeatureEnabled(this.context, 'answers:admin') &&
      !grantsAiAccess(feature => isFeatureEnabled(this.context, feature))
    ) {
      return Promise.resolve();
    }

    const { organization: organizationStore } = this.context.stores;
    const { sidekickOrganization: gateway } = this.context.gateways;

    return gateway
      .fetch()
      .then(() => {
        organization.setSidekickEnabled(true);
        organizationStore.set(organization);
      })
      .catch(err => {
        if (!(err instanceof NotExistError)) {
          throw err;
        }
      });
  }
}
